import { useEffect } from "react";

type PageMeta = {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta({ title, description, keywords, path, jsonLd }: PageMeta) {
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = `${origin}${path ?? window.location.pathname}`;

    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("name", "author", "17吃瓜社区");
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "17吃瓜社区");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta(
      "property",
      "og:image",
      "https://readdy.ai/api/search-image?query=stylized%20editorial%20illustration%20of%20warm%20speech%20bubbles%20and%20a%20glowing%20flame%20symbol%2C%20cream%20background%20with%20terracotta%20and%20amber%20accents%2C%20abstract%20geometric%20shapes%2C%20subtle%20grain%20texture%2C%20minimalist%20magazine%20art%20style&width=1200&height=630&seq=gossip-og-cover&orientation=landscape"
    );

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta(
      "name",
      "twitter:image",
      "https://readdy.ai/api/search-image?query=stylized%20editorial%20illustration%20of%20warm%20speech%20bubbles%20and%20a%20glowing%20flame%20symbol%2C%20cream%20background%20with%20terracotta%20and%20amber%20accents%2C%20abstract%20geometric%20shapes%2C%20subtle%20grain%20texture%2C%20minimalist%20magazine%20art%20style&width=1200&height=630&seq=gossip-og-cover&orientation=landscape"
    );

    const scriptId = "page-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, jsonLd]);
}